// Global Instructions Rule Applied!
//
// Deal Pipeline → Budget tab (owner 2026-11-03, Option A "statement"): the
// capital and cash position of the funds the user follows, as one table —
// the line items as rows in statement order, one column per fund plus a
// Total. The open deal commitments are sub-rows under Commitments, so a deal
// two funds are both in sits on one row with a figure in each column.
//
//   Committed Capital / Called Capital / Uncalled Capital   (capital-call position)
//   Cash Balance / Accounts Payable / Net Cash              (the ledger)
//   Commitments: one sub-row per committed deal              (deal_commitments)
//   Cash Remaining = Net Cash - Commitments
//   Capital Remaining = Uncalled + Net Cash - Commitments
//
// Chrome: the ViewToolbar ribbon with the fund multi-select in its leftSlot
// (per-user pick, cfg default on first open), the standard refresh; one
// FormSection holding an antd <Table size='small'> (the CommitmentsTab
// precedent). Figures render through NumberText.

import React, { useCallback, useMemo } from 'react'
import { Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../../core/hooks/useRefreshOnActivate.ts'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'
import ViewToolbar from '../../../../core/components/ViewToolbar/index.tsx'
import NumberText from '../../../../core/components/NumberText.tsx'
import HelpTip from '../../../../core/components/HelpTip.tsx'
import { TABLE_DEALS } from '../../../../state_manager/globals.ts'
import dayjs from 'dayjs'
import type {
  DealPipelineBudgetFigureKey,
  DealPipelineBudgetFund
} from '../../../../state_manager/data_models/deal_pipeline_budget.ts'
import { useDealPipelineBudget } from '../../hooks/useDealPipelineBudget.ts'

const { Text } = Typography

const formatDate = (value: string): string => dayjs(value).format('MM/DD/YYYY')

type RowKind = 'group' | 'figure' | 'subtotal' | 'deal' | 'remaining' | 'hero'

interface BudgetRow {
  key: string
  kind: RowKind
  label: string
  help?: string
  /** For figure rows: the key read off each fund entry and the totals. */
  figure?: DealPipelineBudgetFigureKey
  /** For deal rows: the amount per fund id, and the deal to open. */
  dealId?: string
  stageName?: string | null
  stageDate?: string | null
  amounts?: Record<string, number>
  total?: number
}

const CASH_REMAINING_HELP = 'Net Cash less the open commitments. Negative means the fund has to call capital before it can fund what it has committed.'
const CAPITAL_REMAINING_HELP = 'Uncalled Capital plus Net Cash, less the open commitments: what the fund can still put to work.'
const COMMITMENTS_HELP = 'Deals in the Committed stage of the origination pipeline, with their commitment to each fund. A booked deal moves to Portfolio and drops out.'
const FUNDS_HELP = 'The funds this tab shows, remembered for you. Clear the pick to go back to the default funds.'

export default function DealPipelineBudgetTab(): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const { budget, loading, selectedFundIds, setSelectedFundIds, refresh } = useDealPipelineBudget()
  const activateRef = useRefreshOnActivate(() => refresh(true))

  const funds: DealPipelineBudgetFund[] = useMemo(() => budget?.funds ?? [], [budget])
  const commitments = useMemo(() => budget?.commitments ?? [], [budget])
  const totals = budget?.totals

  const rows: BudgetRow[] = useMemo(() => {
    const figure = (key: string, kind: RowKind, label: string, fig: DealPipelineBudgetFigureKey, help?: string): BudgetRow => ({
      key,
      kind,
      label,
      figure: fig,
      help
    })
    // one sub-row per deal; a deal two funds share collapses onto one row
    const byDeal = new Map<string, BudgetRow>()
    for (const c of commitments) {
      const amount = c.deal_commitments__amount ?? 0
      const existing = byDeal.get(c.deals__id)
      if (existing) {
        existing.amounts![c.funds__id] = (existing.amounts![c.funds__id] ?? 0) + amount
        existing.total = (existing.total ?? 0) + amount
        continue
      }
      byDeal.set(c.deals__id, {
        key: `deal:${c.deals__id}`,
        kind: 'deal',
        label: c.deals__name || 'Deal',
        dealId: c.deals__id,
        stageName: c.pipeline_stages__name,
        stageDate: c.deals__pipeline_stage_date,
        amounts: { [c.funds__id]: amount },
        total: amount
      })
    }
    const dealRows = [...byDeal.values()].sort((a, b) => a.label.localeCompare(b.label))

    return [
      { key: 'g:capital', kind: 'group', label: 'Capital' },
      figure('committed', 'figure', 'Committed Capital', 'prog__committed'),
      figure('called', 'figure', 'Called Capital', 'prog__called'),
      figure('uncalled', 'subtotal', 'Uncalled Capital', 'prog__uncalled'),
      { key: 'g:cash', kind: 'group', label: 'Cash' },
      figure('cash', 'figure', 'Cash Balance', 'prog__cash'),
      figure('payables', 'figure', 'Accounts Payable', 'prog__payables'),
      figure('net_cash', 'subtotal', 'Net Cash', 'prog__net_cash'),
      { key: 'g:commitments', kind: 'group', label: 'Commitments', help: COMMITMENTS_HELP },
      ...dealRows,
      figure('commitments', 'subtotal', 'Total Commitments', 'prog__commitments'),
      { key: 'g:remaining', kind: 'group', label: 'Remaining' },
      figure('cash_remaining', 'remaining', 'Cash Remaining', 'prog__cash_remaining', CASH_REMAINING_HELP),
      figure('capital_remaining', 'hero', 'Capital Remaining', 'prog__capital_remaining', CAPITAL_REMAINING_HELP)
    ]
  }, [commitments])

  const valueOf = useCallback(
    (row: BudgetRow, fundId: string | null): number | null => {
      if (row.kind === 'group') return null
      if (row.kind === 'deal') {
        if (fundId === null) return row.total ?? 0
        return row.amounts?.[fundId] ?? null
      }
      const source = fundId === null ? totals : funds.find((f) => f.funds__id === fundId)
      const value = row.figure ? source?.[row.figure] : null
      return value ?? 0
    },
    [funds, totals]
  )

  const renderFigure = useCallback(
    (row: BudgetRow, fundId: string | null): React.ReactNode => {
      const value = valueOf(row, fundId)
      if (value === null) return row.kind === 'deal' ? <Text type='secondary'>—</Text> : null
      const strong = row.kind === 'subtotal' || row.kind === 'remaining' || row.kind === 'hero'
      const danger = row.kind === 'remaining' && value < 0
      return (
        <NumberText
          value={value}
          wrap={(n) => (danger ? <Text strong type='danger'>{n}</Text> : strong ? <Text strong>{n}</Text> : n)}
        />
      )
    },
    [valueOf]
  )

  const columns: ColumnsType<BudgetRow> = useMemo(() => {
    const labelColumn: ColumnsType<BudgetRow>[number] = {
      title: '',
      dataIndex: 'label',
      key: 'label',
      width: 320,
      onCell: (row) => (row.kind === 'group' ? { colSpan: funds.length + 2 } : {}),
      render: (_: unknown, row) => {
        if (row.kind === 'group') {
          return (
            <Text strong className='text-xs uppercase tracking-wide text-gray-600'>
              {row.label}
              <HelpTip title={row.help} />
            </Text>
          )
        }
        if (row.kind === 'deal') {
          const since = row.stageDate ? ` since ${formatDate(row.stageDate)}` : ''
          return (
            <span className='pl-4'>
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => openFormInTab(TABLE_DEALS, row.dealId ?? null, row.label)}
              >
                {row.label}
              </button>
              {row.stageName && (
                <Text type='secondary' className='ml-2 text-xs'>
                  · {row.stageName}
                  {since}
                </Text>
              )}
            </span>
          )
        }
        const strong = row.kind !== 'figure'
        return (
          <span>
            {strong ? <Text strong>{row.label}</Text> : row.label}
            <HelpTip title={row.help} />
          </span>
        )
      }
    }
    const fundColumns: ColumnsType<BudgetRow> = funds.map((f) => ({
      title: f.funds__name ?? 'Fund',
      key: f.funds__id,
      align: 'right' as const,
      onCell: (row) => (row.kind === 'group' ? { colSpan: 0 } : {}),
      render: (_: unknown, row) => renderFigure(row, f.funds__id)
    }))
    const totalColumn: ColumnsType<BudgetRow>[number] = {
      title: 'Total',
      key: 'total',
      align: 'right' as const,
      onCell: (row) => (row.kind === 'group' ? { colSpan: 0 } : {}),
      render: (_: unknown, row) => renderFigure(row, null)
    }
    // a single fund needs no Total column: it would repeat the fund's own
    return funds.length > 1 ? [labelColumn, ...fundColumns, totalColumn] : [labelColumn, ...fundColumns]
  }, [funds, renderFigure, openFormInTab])

  const rowClassName = useCallback((row: BudgetRow): string => {
    switch (row.kind) {
      case 'group':
        return 'bg-gray-50'
      case 'subtotal':
      case 'remaining':
        return 'bg-gray-50 border-t'
      case 'hero':
        return 'bg-blue-50'
      default:
        return ''
    }
  }, [])

  const asOf = budget?.as_of ? formatDate(budget.as_of) : null
  const emptyText = funds.length === 0 && !loading ? 'No funds to show — pick a fund in the ribbon.' : undefined

  return (
    <div ref={activateRef} className='h-full overflow-y-auto pr-1'>
      <ViewToolbar
        moduleKey='deal-pipeline-budget'
        searchEnabled={false}
        addButtonEnabled={false}
        onRefreshData={() => refresh(false)}
        leftSlot={
          <div className='flex items-center gap-2'>
            <span className='text-white text-xs'>
              Funds
              <HelpTip title={FUNDS_HELP} />
            </span>
            <FundSelect
              mode='multiple'
              size='small'
              value={selectedFundIds}
              onChange={(ids: string[] | null | undefined) => setSelectedFundIds(ids ?? [])}
              placeholder='Default funds'
              style={{ minWidth: 320 }}
              data-testid='budget-fund-select'
            />
          </div>
        }
      />
      <FormSection
        title='Capital position'
        titleColor='blue'
        scrollable={false}
        titleSuffix={asOf ? <Text type='secondary' className='text-xs'>· as of {asOf}</Text> : undefined}
        loading={loading && !budget}
      >
        <Table<BudgetRow>
          size='small'
          pagination={false}
          loading={loading && !!budget}
          dataSource={rows}
          columns={columns}
          rowKey='key'
          rowClassName={rowClassName}
          tableLayout='auto'
          locale={emptyText ? { emptyText } : undefined}
          data-testid='budget-table'
        />
      </FormSection>
    </div>
  )
}
